import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Calendar,
  Users,
  Microscope,
  TrendingUp,
  ChevronDown,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Zap,
  MapPin,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';

const COLORS = {
  primary: '#2E8B98',
  teal: '#2E8B98',
  green: '#62BB46',
  orange: '#F26522',
  red: '#DC3545',
  dark: '#256F7A',
  light: '#F8F9FA',
  white: '#FFFFFF',
  border: '#e2e8f0',
  text: '#2d3748',
  textLight: '#718096',
};

const CATEGORY_OPTIONS = [
  'General',
  'Stem Cell',
  'Oncology',
  'Cardiology',
  'Neurology',
  'Immunology',
  'Genomics',
  'Regenerative Medicine',
  'Clinical Trial',
  'Pharmacology',
  'Other',
];

const STATUS_OPTIONS = [
  'Planning',
  'Active',
  'Recruiting',
  'Completed',
  'Suspended',
  'Terminated',
  'Published',
];

const PHASE_OPTIONS = [
  'Preclinical',
  'Phase I',
  'Phase II',
  'Phase III',
  'Phase IV',
  'Observational',
  'N/A',
];

const FINDING_TYPES = [
  'Efficacy',
  'Safety',
  'Adverse Event',
  'Protocol Deviation',
  'Milestone',
  'Enrollment Update',
];

const STATUS_COLORS = {
  Planning: { bg: '#f1f5f9', text: '#475569' },
  Active: { bg: '#dcfce7', text: '#15803d' },
  Recruiting: { bg: '#dbeafe', text: '#1d4ed8' },
  Completed: { bg: '#e0e7ff', text: '#4338ca' },
  Suspended: { bg: '#fef3c7', text: '#b45309' },
  Terminated: { bg: '#fef2f2', text: '#DC3545' },
  Published: { bg: '#f0fdf4', text: '#166534' },
};

const SIGNIFICANCE_COLORS = {
  Low: { bg: '#f1f5f9', text: '#475569' },
  Medium: { bg: '#fef3c7', text: '#b45309' },
  High: { bg: '#dcfce7', text: '#15803d' },
  Critical: { bg: '#fef2f2', text: '#DC3545' },
};

// KPI Card Component
function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div
      style={{
        background: COLORS.white,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ marginBottom: 12, color }}>
        <Icon size={24} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.dark, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textLight }}>{label}</div>
    </div>
  );
}

// New Study Modal
function NewStudyModal({ isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'General',
    status: 'Planning',
    phase: 'N/A',
    principalInvestigator: '',
    irbNumber: '',
    fundingSource: '',
    budget: '',
    enrollmentTarget: '',
    startDate: '',
    endDate: '',
    tags: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.status) newErrors.status = 'Status is required';
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      ...formData,
      budget: formData.budget ? parseInt(formData.budget, 10) : null,
      enrollmentTarget: formData.enrollmentTarget
        ? parseInt(formData.enrollmentTarget, 10)
        : null,
      tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()) : [],
    };

    onSave(submitData);
    setFormData({
      title: '',
      description: '',
      category: 'General',
      status: 'Planning',
      phase: 'N/A',
      principalInvestigator: '',
      irbNumber: '',
      fundingSource: '',
      budget: '',
      enrollmentTarget: '',
      startDate: '',
      endDate: '',
      tags: '',
      notes: '',
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: 32,
          maxWidth: 600,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>
            Create New Research Study
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.title ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="Study title"
            />
            {errors.title && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.title}</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.category ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            {errors.category && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.category}</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.status ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            {errors.status && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.status}</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Phase
            </label>
            <select
              name="phase"
              value={formData.phase}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              {PHASE_OPTIONS.map((phase) => (
                <option key={phase} value={phase}>
                  {phase}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 100,
              resize: 'vertical',
            }}
            placeholder="Study description"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Principal Investigator
            </label>
            <input
              type="text"
              name="principalInvestigator"
              value={formData.principalInvestigator}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="PI name"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              IRB Number
            </label>
            <input
              type="text"
              name="irbNumber"
              value={formData.irbNumber}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="IRB-XXXX"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Funding Source
            </label>
            <input
              type="text"
              name="fundingSource"
              value={formData.fundingSource}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="Funding source"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Budget ($)
            </label>
            <input
              type="number"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="0"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Enrollment Target
            </label>
            <input
              type="number"
              name="enrollmentTarget"
              value={formData.enrollmentTarget}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="0"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Tags (comma separated)
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="tag1, tag2, tag3"
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 80,
              resize: 'vertical',
            }}
            placeholder="Additional notes"
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.white,
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = COLORS.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.white;
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.background = COLORS.dark;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.background = COLORS.primary;
            }}
          >
            {isLoading ? 'Saving...' : 'Save Study'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Condition Modal
function AddConditionModal({ isOpen, onClose, onSave, isLoading, studyId }) {
  const [formData, setFormData] = useState({
    patientId: '',
    icd10Code: '',
    snomedCode: '',
    conditionDescription: '',
    enrolledDate: '',
    status: 'Active',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [showPatients, setShowPatients] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePatientSearch = async (value) => {
    setPatientSearch(value);
    if (value.length > 1 && window.zignagenixAPI) {
      try {
        const results = await window.zignagenixAPI.searchPatients(value);
        setPatients(Array.isArray(results) ? results : []);
        setShowPatients(true);
      } catch (error) {
        console.error('Patient search error:', error);
        setPatients([]);
      }
    } else {
      setPatients([]);
      setShowPatients(false);
    }
  };

  const selectPatient = (patient) => {
    setFormData((prev) => ({ ...prev, patientId: patient.id }));
    setPatientSearch(`${patient.firstName} ${patient.lastName}`);
    setShowPatients(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patientId) newErrors.patientId = 'Patient is required';
    if (!formData.conditionDescription.trim()) newErrors.conditionDescription = 'Condition is required';
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      studyId,
      ...formData,
    });
    setFormData({
      patientId: '',
      icd10Code: '',
      snomedCode: '',
      conditionDescription: '',
      enrolledDate: '',
      status: 'Active',
      notes: '',
    });
    setPatientSearch('');
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: 32,
          maxWidth: 500,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>Add Condition</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: 16, position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Patient *
          </label>
          <input
            type="text"
            value={patientSearch}
            onChange={(e) => handlePatientSearch(e.target.value)}
            onFocus={() => patientSearch.length > 0 && setShowPatients(true)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.patientId ? COLORS.red : COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
            placeholder="Search patient"
          />
          {showPatients && patients.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                marginTop: 4,
                maxHeight: 150,
                overflowY: 'auto',
                zIndex: 1002,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
            >
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  onClick={() => selectPatient(patient)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontSize: 13,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.light;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.white;
                  }}
                >
                  {patient.firstName} {patient.lastName} ({patient.mrn})
                </div>
              ))}
            </div>
          )}
          {errors.patientId && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.patientId}</div>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Condition Description *
          </label>
          <textarea
            name="conditionDescription"
            value={formData.conditionDescription}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.conditionDescription ? COLORS.red : COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 70,
              resize: 'vertical',
            }}
            placeholder="Describe the condition"
          />
          {errors.conditionDescription && (
            <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.conditionDescription}</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              ICD-10 Code
            </label>
            <input
              type="text"
              name="icd10Code"
              value={formData.icd10Code}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="E.g., I10"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              SNOMED Code
            </label>
            <input
              type="text"
              name="snomedCode"
              value={formData.snomedCode}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="E.g., 38341003"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Enrolled Date
            </label>
            <input
              type="date"
              name="enrolledDate"
              value={formData.enrolledDate}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Withdrawn">Withdrawn</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 60,
              resize: 'vertical',
            }}
            placeholder="Additional notes"
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.white,
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = COLORS.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.white;
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.background = COLORS.dark;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.background = COLORS.primary;
            }}
          >
            {isLoading ? 'Saving...' : 'Add Condition'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Finding Modal
function AddFindingModal({ isOpen, onClose, onSave, isLoading, studyId }) {
  const [formData, setFormData] = useState({
    title: '',
    findingType: 'Milestone',
    description: '',
    significance: 'Medium',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      studyId,
      ...formData,
    });
    setFormData({
      title: '',
      findingType: 'Milestone',
      description: '',
      significance: 'Medium',
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: 32,
          maxWidth: 500,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>Add Finding</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.title ? COLORS.red : COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
            placeholder="Finding title"
          />
          {errors.title && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.title}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Finding Type
            </label>
            <select
              name="findingType"
              value={formData.findingType}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              {FINDING_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Significance
            </label>
            <select
              name="significance"
              value={formData.significance}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.description ? COLORS.red : COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 100,
              resize: 'vertical',
            }}
            placeholder="Detailed finding description"
          />
          {errors.description && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.description}</div>}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.white,
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = COLORS.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.white;
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.background = COLORS.dark;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.background = COLORS.primary;
            }}
          >
            {isLoading ? 'Saving...' : 'Add Finding'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Study Detail Drawer
function StudyDetailDrawer({ study, isOpen, onClose, onConditionAdded, onFindingAdded }) {
  const [activeTab, setActiveTab] = useState('conditions');
  const [conditions, setConditions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [matchedPatients, setMatchedPatients] = useState([]);
  const [showMatchedPatients, setShowMatchedPatients] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [showAddFinding, setShowAddFinding] = useState(false);

  useEffect(() => {
    if (isOpen && study && window.zignagenixAPI) {
      loadStudyDetails();
    }
  }, [isOpen, study]);

  const loadStudyDetails = async () => {
    try {
      setLoading(true);
      const [conditionsData, findingsData] = await Promise.all([
        window.zignagenixAPI.getStudyConditions(study.id),
        window.zignagenixAPI.getResearchFindings(study.id),
      ]);
      setConditions(Array.isArray(conditionsData) ? conditionsData : []);
      setFindings(Array.isArray(findingsData) ? findingsData : []);
    } catch (error) {
      console.error('Error loading study details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFindMatchingPatients = async () => {
    try {
      setLoading(true);
      const results = await window.zignagenixAPI.matchResearchPatients(study.id);
      setMatchedPatients(Array.isArray(results) ? results : []);
      setShowMatchedPatients(true);
    } catch (error) {
      console.error('Error finding matching patients:', error);
      setMatchedPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConditionAdded = (conditionData) => {
    if (window.zignagenixAPI) {
      window.zignagenixAPI
        .createStudyCondition(conditionData)
        .then(() => {
          setShowAddCondition(false);
          loadStudyDetails();
          if (onConditionAdded) onConditionAdded();
        })
        .catch((error) => console.error('Error adding condition:', error));
    }
  };

  const handleFindingAdded = (findingData) => {
    if (window.zignagenixAPI) {
      window.zignagenixAPI
        .createResearchFinding(findingData)
        .then(() => {
          setShowAddFinding(false);
          loadStudyDetails();
          if (onFindingAdded) onFindingAdded();
        })
        .catch((error) => console.error('Error adding finding:', error));
    }
  };

  if (!isOpen || !study) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 500,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.2s',
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: 800,
          height: '100vh',
          background: COLORS.white,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
          zIndex: 501,
          overflowY: 'auto',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s',
        }}
      >
        <div style={{ padding: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 24 }}>
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: 24, fontWeight: 700, color: COLORS.dark }}>
                {study.title}
              </h2>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  style={{
                    padding: '4px 12px',
                    background: STATUS_COLORS[study.status]?.bg,
                    color: STATUS_COLORS[study.status]?.text,
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {study.status}
                </span>
                <span style={{ fontSize: 13, color: COLORS.textLight }}>{study.category}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: COLORS.textLight,
                fontSize: 20,
              }}
            >
              <X size={24} />
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: 24,
              padding: 16,
              background: COLORS.light,
              borderRadius: 8,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 4 }}>Principal Investigator</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                {study.principalInvestigator || 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 4 }}>Phase</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>{study.phase || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 4 }}>Start Date</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                {study.startDate ? new Date(study.startDate).toLocaleDateString() : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 4 }}>Enrollment</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                {study.currentEnrollment || 0} / {study.enrollmentTarget || 'TBD'}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: COLORS.dark }}>Description</h3>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
              {study.description || 'No description provided'}
            </p>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                gap: 0,
                borderBottom: `1px solid ${COLORS.border}`,
                marginBottom: 20,
              }}
            >
              {['conditions', 'findings', 'patients'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    fontSize: 13,
                    fontWeight: activeTab === tab ? 700 : 600,
                    color: activeTab === tab ? COLORS.primary : COLORS.textLight,
                    cursor: 'pointer',
                    borderBottom: activeTab === tab ? `3px solid ${COLORS.primary}` : 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab) e.target.style.color = COLORS.text;
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab) e.target.style.color = COLORS.textLight;
                  }}
                >
                  {tab === 'conditions' && 'Conditions'}
                  {tab === 'findings' && 'Findings'}
                  {tab === 'patients' && 'Patient Matches'}
                </button>
              ))}
            </div>

            {activeTab === 'conditions' && (
              <div>
                <button
                  onClick={() => setShowAddCondition(true)}
                  style={{
                    padding: '8px 16px',
                    background: COLORS.primary,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.dark;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.primary;
                  }}
                >
                  <Plus size={14} /> Add Condition
                </button>

                {conditions.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>
                            Condition
                          </th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>
                            ICD-10
                          </th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>
                            SNOMED
                          </th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>Patient</th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conditions.map((condition, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 8, color: COLORS.text }}>{condition.conditionDescription}</td>
                            <td style={{ padding: 8, color: COLORS.textLight, fontFamily: 'monospace', fontSize: 12 }}>
                              {condition.icd10Code || '-'}
                            </td>
                            <td style={{ padding: 8, color: COLORS.textLight, fontFamily: 'monospace', fontSize: 12 }}>
                              {condition.snomedCode || '-'}
                            </td>
                            <td style={{ padding: 8, color: COLORS.text }}>
                              {condition.patientName || 'Unknown'}
                            </td>
                            <td style={{ padding: 8 }}>
                              <span
                                style={{
                                  padding: '2px 8px',
                                  background: STATUS_COLORS[condition.status]?.bg,
                                  color: STATUS_COLORS[condition.status]?.text,
                                  borderRadius: 3,
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {condition.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    No conditions linked yet
                  </div>
                )}
              </div>
            )}

            {activeTab === 'findings' && (
              <div>
                <button
                  onClick={() => setShowAddFinding(true)}
                  style={{
                    padding: '8px 16px',
                    background: COLORS.primary,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.dark;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.primary;
                  }}
                >
                  <Plus size={14} /> Add Finding
                </button>

                {findings.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {findings.map((finding, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: 12,
                          background: COLORS.light,
                          borderRadius: 6,
                          borderLeft: `4px solid ${COLORS.primary}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: COLORS.dark }}>
                            {finding.title}
                          </h4>
                          <span
                            style={{
                              padding: '2px 8px',
                              background: SIGNIFICANCE_COLORS[finding.significance]?.bg,
                              color: SIGNIFICANCE_COLORS[finding.significance]?.text,
                              borderRadius: 3,
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {finding.significance}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 6 }}>
                          {finding.findingType}
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                          {finding.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    No findings recorded yet
                  </div>
                )}
              </div>
            )}

            {activeTab === 'patients' && (
              <div>
                <button
                  onClick={handleFindMatchingPatients}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    background: COLORS.green,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.background = '#558a38';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.target.style.background = COLORS.green;
                  }}
                >
                  <Search size={14} /> {loading ? 'Finding Patients...' : 'Find Matching Patients'}
                </button>

                {showMatchedPatients && matchedPatients.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>
                            Patient
                          </th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>MRN</th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>
                            Matching Codes
                          </th>
                          <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: COLORS.dark }}>Match Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchedPatients.map((patient, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 8, color: COLORS.text, fontWeight: 600 }}>
                              {patient.firstName} {patient.lastName}
                            </td>
                            <td style={{ padding: 8, color: COLORS.textLight, fontFamily: 'monospace' }}>
                              {patient.mrn}
                            </td>
                            <td style={{ padding: 8, color: COLORS.text }}>{patient.matchingCodes?.join(', ') || 'N/A'}</td>
                            <td style={{ padding: 8, color: COLORS.text }}>
                              <div
                                style={{
                                  width: 60,
                                  height: 6,
                                  background: COLORS.light,
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    width: `${Math.min((patient.matchScore || 0) * 100, 100)}%`,
                                    background: COLORS.green,
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : showMatchedPatients && matchedPatients.length === 0 ? (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    No matching patients found
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    Click "Find Matching Patients" to discover eligible patients
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddConditionModal
        isOpen={showAddCondition}
        onClose={() => setShowAddCondition(false)}
        onSave={handleConditionAdded}
        isLoading={loading}
        studyId={study.id}
      />

      <AddFindingModal
        isOpen={showAddFinding}
        onClose={() => setShowAddFinding(false)}
        onSave={handleFindingAdded}
        isLoading={loading}
        studyId={study.id}
      />
    </>
  );
}

// Main Component
function ResearchStudiesPage({ user, onNavigate }) {
  const [activeTab, setActiveTab] = useState('studies');
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    totalStudies: 0,
    activeStudies: 0,
    enrolledPatients: 0,
    publishedFindings: 0,
  });

  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: '',
  });

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });

  const [showNewStudyModal, setShowNewStudyModal] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!window.zignagenixAPI) {
        setError('API not available');
        return;
      }

      const [summaryData, studiesData] = await Promise.all([
        window.zignagenixAPI.getResearchSummary(),
        window.zignagenixAPI.getResearchStudies(filters),
      ]);

      setSummary(
        summaryData || {
          totalStudies: 0,
          activeStudies: 0,
          enrolledPatients: 0,
          publishedFindings: 0,
        }
      );
      setStudies(Array.isArray(studiesData) ? studiesData : []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load research data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudy = async (formData) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        await window.zignagenixAPI.createResearchStudy(formData);
        setShowNewStudyModal(false);
        loadData();
      }
    } catch (err) {
      console.error('Error creating study:', err);
      setError('Failed to create study');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const filteredStudies = studies.filter((study) => {
    let match = true;
    if (filters.category && study.category !== filters.category) match = false;
    if (filters.status && study.status !== filters.status) match = false;
    if (
      filters.search &&
      !study.title.toLowerCase().includes(filters.search.toLowerCase()) &&
      !study.description?.toLowerCase().includes(filters.search.toLowerCase())
    ) {
      match = false;
    }
    return match;
  });

  const handleViewStudy = (study) => {
    setSelectedStudy(study);
    setShowDetailDrawer(true);
  };

  return (
    <div style={{ background: COLORS.light, minHeight: '100vh', padding: 32 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.teal})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.white,
              }}
            >
              <Microscope size={28} />
            </div>
            <div>
              <h1 style={{ margin: '0 0 4px 0', fontSize: 28, fontWeight: 700, color: COLORS.dark }}>
                Research & Studies
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: COLORS.textLight,
                }}
              >
                Business Intelligence — Cross-referenced to Patient Conditions
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={loadData}
              disabled={loading}
              style={{
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s',
              }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <PrintButton />
          </div>
        </div>

        {/* Date filters */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>Date Range:</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
            style={{
              padding: '6px 10px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <span style={{ color: COLORS.textLight }}>to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
            style={{
              padding: '6px 10px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 24,
            background: '#fef2f2',
            border: `1px solid ${COLORS.red}`,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: COLORS.red,
            fontSize: 13,
          }}
        >
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* KPI Summary Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}
      >
        <KPICard
          label="Total Studies"
          value={summary.totalStudies || 0}
          icon={Microscope}
          color={COLORS.primary}
        />
        <KPICard label="Active Studies" value={summary.activeStudies || 0} icon={Zap} color={COLORS.green} />
        <KPICard
          label="Enrolled Patients"
          value={summary.enrolledPatients || 0}
          icon={Users}
          color={COLORS.orange}
        />
        <KPICard
          label="Published Findings"
          value={summary.publishedFindings || 0}
          icon={FileText}
          color={COLORS.red}
        />
      </div>

      {/* Tabs and Content */}
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ borderBottom: `1px solid ${COLORS.border}`, padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { id: 'studies', label: 'Studies' },
              { id: 'findings', label: 'Findings Overview' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '16px 20px',
                  background: 'none',
                  border: 'none',
                  fontSize: 14,
                  fontWeight: activeTab === tab.id ? 700 : 600,
                  color: activeTab === tab.id ? COLORS.primary : COLORS.textLight,
                  cursor: 'pointer',
                  borderBottom: activeTab === tab.id ? `3px solid ${COLORS.primary}` : 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) e.target.style.color = COLORS.text;
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) e.target.style.color = COLORS.textLight;
                }}
              >
                {tab.label}
              </button>
            ))}
            {/* Cross-link to Research Database */}
            <button
              onClick={() => onNavigate && onNavigate('researchdb')}
              style={{
                padding: '16px 20px',
                background: 'none',
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.textLight,
                cursor: 'pointer',
                borderBottom: 'none',
                transition: 'all 0.2s',
                marginLeft: 'auto',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = COLORS.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.textLight; }}
              title="Go to Research Database (Protocols & Treatments)"
            >
              Research Database →
            </button>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {activeTab === 'studies' && (
            <div>
              {/* Filters and New Study Button */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  style={{
                    padding: '8px 12px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 6,
                    fontSize: 13,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">All Categories</option>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>

                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  style={{
                    padding: '8px 12px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 6,
                    fontSize: 13,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">All Statuses</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>

                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: 10, top: 8, color: COLORS.textLight }} />
                  <input
                    type="text"
                    placeholder="Search studies..."
                    value={filters.search}
                    onChange={handleSearchChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px 8px 36px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <button
                  onClick={() => setShowNewStudyModal(true)}
                  style={{
                    padding: '10px 16px',
                    background: COLORS.primary,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.dark;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.primary;
                  }}
                >
                  <Plus size={14} /> New Study
                </button>
              </div>

              {/* Studies Table */}
              {loading && !studies.length ? (
                <div
                  style={{
                    padding: 64,
                    textAlign: 'center',
                    color: COLORS.textLight,
                    fontSize: 14,
                  }}
                >
                  Loading studies...
                </div>
              ) : filteredStudies.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Title
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Category
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Status
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Phase
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>PI</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Enrollment
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Start Date
                        </th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 700, color: COLORS.dark }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudies.map((study, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: `1px solid ${COLORS.border}`,
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = COLORS.light;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                          }}
                        >
                          <td style={{ padding: '12px 8px', color: COLORS.dark, fontWeight: 600 }}>
                            {study.title}
                          </td>
                          <td style={{ padding: '12px 8px', color: COLORS.text }}>{study.category}</td>
                          <td style={{ padding: '12px 8px' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                background: STATUS_COLORS[study.status]?.bg,
                                color: STATUS_COLORS[study.status]?.text,
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {study.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', color: COLORS.text }}>{study.phase || 'N/A'}</td>
                          <td style={{ padding: '12px 8px', color: COLORS.text }}>
                            {study.principalInvestigator || 'N/A'}
                          </td>
                          <td style={{ padding: '12px 8px', color: COLORS.text }}>
                            {study.currentEnrollment || 0} / {study.enrollmentTarget || 'TBD'}
                          </td>
                          <td style={{ padding: '12px 8px', color: COLORS.text, fontSize: 12 }}>
                            {study.startDate ? new Date(study.startDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleViewStudy(study)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: COLORS.primary,
                                cursor: 'pointer',
                                padding: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = COLORS.dark;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = COLORS.primary;
                              }}
                            >
                              <Eye size={14} /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    padding: 64,
                    textAlign: 'center',
                    color: COLORS.textLight,
                    fontSize: 14,
                  }}
                >
                  No studies found matching your filters
                </div>
              )}
            </div>
          )}

          {activeTab === 'findings' && (
            <div>
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  background: COLORS.light,
                  borderRadius: 8,
                  color: COLORS.text,
                  fontSize: 14,
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <TrendingUp size={32} color={COLORS.primary} style={{ margin: '0 auto 12px' }} />
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700, color: COLORS.dark }}>
                  Findings Overview
                </h3>
                <p style={{ margin: 0, color: COLORS.textLight, fontSize: 13 }}>
                  View aggregated findings across all research studies. Navigate to individual studies to add and manage
                  findings.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <NewStudyModal
        isOpen={showNewStudyModal}
        onClose={() => setShowNewStudyModal(false)}
        onSave={handleCreateStudy}
        isLoading={loading}
      />

      <StudyDetailDrawer
        study={selectedStudy}
        isOpen={showDetailDrawer}
        onClose={() => setShowDetailDrawer(false)}
        onConditionAdded={loadData}
        onFindingAdded={loadData}
      />
    </div>
  );
}

export default ResearchStudiesPage;
